export const READ = Object.freeze({
  SESSION: "owner_session",
  DASHBOARD: "dashboard",
  SECTIONS: "sections",
  SECTION: "section",
  PAGE: "page",
  PRODUCTS: "products",
  PRODUCT_CONFIGURATION: "product_configuration",
  MEDIA: "media",
  STOREFRONT_SETTINGS: "storefront_settings",
  STOREFRONT_HOME: "storefront_home",
  STOREFRONT_DEPARTMENTS: "storefront_departments",
  STOREFRONT_DEPARTMENT: "storefront_department",
  STOREFRONT_CATALOGUE: "storefront_catalogue",
  STOREFRONT_PAGE_REGISTRY: "storefront_page_registry",
  OWNERSHIP: "ownership",
  MODERATION_REPORTS: "moderation_reports",
});

export const MUTATION = Object.freeze({
  CREATE_SECTION: "create_section",
  UPDATE_SECTION: "update_section",
  ADD_PAGE: "add_page",
  UPDATE_PAGE: "update_page",
  REMOVE_PAGE: "remove_page",
  CREATE_PRODUCT: "create_product",
  UPDATE_PRODUCT: "update_product",
  SAVE_PRODUCT_CONFIGURATION: "save_product_configuration",
  SET_SECTION_DEFAULT_MEDIA: "set_section_default_media",
  DUPLICATE_PRODUCT: "duplicate_product",
  PRODUCT_STATUS: "product_status",
  ASSIGN_SLOT: "assign_slot",
  CLEAR_SLOT: "clear_slot",
  MOVE_SLOT: "move_slot",
  ARCHIVE_MEDIA: "archive_media",
  UPDATE_STOREFRONT_SETTINGS: "update_storefront_settings",
  UPDATE_STOREFRONT_HOME: "update_storefront_home",
  CREATE_STOREFRONT_HUB: "create_storefront_hub",
  UPDATE_STOREFRONT_DEPARTMENT: "update_storefront_department",
  CREATE_STOREFRONT_DEPARTMENT_CARD: "create_storefront_department_card",
  CLEAR_STOREFRONT_DEPARTMENT_CARD: "clear_storefront_department_card",
  UPDATE_STOREFRONT_DEPARTMENT_CARD: "update_storefront_department_card",
  CREATE_STOREFRONT_CATALOGUE: "create_storefront_catalogue",
  UPDATE_STOREFRONT_CATALOGUE: "update_storefront_catalogue",
  UPDATE_STOREFRONT_FACET: "update_storefront_facet",
  UPDATE_STOREFRONT_BANNER: "update_storefront_banner",
  MODERATE_REPORT: "moderate_report",
});

// The Owner CMS is part of the existing VISINVOKE company Admin. Keeping its
// API beneath /v1/staff ensures it uses the same MFA-verified staff cookie
// transport as /v1/staff/me; there is no separate shop identity or login.
const OWNER_PREFIX = "/v1/staff/virtual-shop/owner";
const MAX_MEDIA_BYTES = 8 * 1024 * 1024;
const MAX_IMPORT_BYTES = 25 * 1024 * 1024;
const IMPORT_MODES = new Set(["create", "update"]);
const MEDIA_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
const ID = /^[A-Za-z0-9_-]{1,64}$/;
const PREVIEW_TOKEN = /^[A-Za-z0-9._-]{1,4096}$/;

const failure = (state = "unavailable", reason = "unavailable", status = null, message = null) => ({
  ok: false, state, reason, status, message, data: null,
});

function identifier(value) {
  return typeof value === "string" && ID.test(value) ? value : null;
}

function previewToken(value) {
  return typeof value === "string" && PREVIEW_TOKEN.test(value) ? value : null;
}

function importFile(value) {
  if (!value || typeof value.name !== "string" || !/[.]csv$/i.test(value.name.trim())) return null;
  if (!Number.isSafeInteger(value.size) || value.size <= 0 || value.size > MAX_IMPORT_BYTES) return null;
  return value;
}

function customerLookup(value) {
  const clean = typeof value === "string" ? value.trim() : "";
  const hasControlCharacter = [...clean].some((character) => {
    const code = character.charCodeAt(0); return code < 32 || code === 127;
  });
  return clean && clean.length <= 254 && !hasControlCharacter ? clean : null;
}

export function ownerMediaContentUrl(media, apiBase = process.env.REACT_APP_API_URL || "https://api.visinvoke.com") {
  const base = typeof apiBase === "string" ? apiBase.trim().replace(/\/$/, "") : "";
  if (typeof media?.url === "string" && media.url.trim()) {
    const url = media.url.trim();
    if (/^https?:\/\//i.test(url)) return url;
    if (base && /^\/v1\/virtual-shop\/catalogue\/media\/[A-Za-z0-9_-]{1,64}\/content$/.test(url)) {
      return `${base}${url}`;
    }
  }
  const id = identifier(typeof media === "string" ? media : media?.id);
  if (!id || !base) return null;
  return `${base}${OWNER_PREFIX}/media/${id}/content`;
}

function fixedPayload(value, fields, requireRevision = false) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  if (Object.keys(value).some((key) => !fields.includes(key))) return null;
  if (requireRevision && (!Number.isInteger(value.revision) || value.revision < 1)) return null;
  return value;
}

function query(args = {}, fields = []) {
  const params = new URLSearchParams();
  for (const field of fields) {
    const value = args[field];
    if (value !== undefined && value !== null && value !== "") params.set(field, String(value));
  }
  const encoded = params.toString();
  return encoded ? `?${encoded}` : "";
}

function readPath(operation, args = {}) {
  switch (operation) {
    case READ.SESSION: return `${OWNER_PREFIX}/session`;
    case READ.DASHBOARD: return `${OWNER_PREFIX}/dashboard`;
    case READ.SECTIONS:
      return `${OWNER_PREFIX}/sections${query(args, ["page", "pageSize", "search", "status"])}`;
    case READ.SECTION: {
      const id = identifier(args.sectionId);
      return id ? `${OWNER_PREFIX}/sections/${id}` : null;
    }
    case READ.PAGE: {
      const id = identifier(args.pageId);
      return id ? `${OWNER_PREFIX}/pages/${id}` : null;
    }
    case READ.PRODUCTS:
      return `${OWNER_PREFIX}/products${query(args, ["page", "pageSize", "search", "status", "placement"])}`;
    case READ.PRODUCT_CONFIGURATION: {
      const id = identifier(args.productId);
      return id ? `${OWNER_PREFIX}/products/${id}/configuration` : null;
    }
    case READ.MEDIA:
      return `${OWNER_PREFIX}/media${query(args, ["page", "pageSize", "search", "status"])}`;
    case READ.STOREFRONT_SETTINGS:
      return `${OWNER_PREFIX}/storefront/settings`;
    case READ.STOREFRONT_HOME:
      return `${OWNER_PREFIX}/storefront/home`;
    case READ.STOREFRONT_DEPARTMENTS:
      return `${OWNER_PREFIX}/storefront/departments`;
    case READ.STOREFRONT_DEPARTMENT: {
      const key = identifier(args.departmentKey);
      return key ? `${OWNER_PREFIX}/storefront/departments/${key}` : null;
    }
    case READ.STOREFRONT_CATALOGUE: {
      const departmentKey = identifier(args.departmentKey);
      const catalogueKey = identifier(args.catalogueKey);
      return departmentKey && catalogueKey
        ? `${OWNER_PREFIX}/storefront/catalogues/${departmentKey}/${catalogueKey}` : null;
    }
    case READ.STOREFRONT_PAGE_REGISTRY:
      return `${OWNER_PREFIX}/storefront/page-registry`;
    case READ.OWNERSHIP: {
      const customer = customerLookup(args.customer);
      const page = Number(args.page);
      const pageSize = Number(args.pageSize);
      return customer && Number.isInteger(page) && page >= 1
        && Number.isInteger(pageSize) && pageSize >= 1 && pageSize <= 50
        ? `${OWNER_PREFIX}/ownership${query({ customer, page, pageSize }, ["customer", "page", "pageSize"])}`
        : null;
    }
    case READ.MODERATION_REPORTS:
      return `${OWNER_PREFIX}/community/reports${query(args, ["page", "pageSize", "status"])}`;
    default: return null;
  }
}

function mutationSpec(operation, args = {}, payload = {}) {
  const id = (field) => identifier(args[field]);
  let body = null;
  switch (operation) {
    case MUTATION.CREATE_SECTION:
      return { method: "POST", path: `${OWNER_PREFIX}/sections`, body: payload };
    case MUTATION.UPDATE_SECTION:
      return id("sectionId") && { method: "PATCH", path: `${OWNER_PREFIX}/sections/${id("sectionId")}`, body: payload };
    case MUTATION.ADD_PAGE:
      return id("sectionId") && { method: "POST", path: `${OWNER_PREFIX}/sections/${id("sectionId")}/pages`, body: payload };
    case MUTATION.UPDATE_PAGE:
      return id("pageId") && { method: "PATCH", path: `${OWNER_PREFIX}/pages/${id("pageId")}`, body: payload };
    case MUTATION.REMOVE_PAGE:
      return id("pageId") && { method: "DELETE", path: `${OWNER_PREFIX}/pages/${id("pageId")}`, body: null };
    case MUTATION.CREATE_PRODUCT:
      return args.slotId === undefined
        ? { method: "POST", path: `${OWNER_PREFIX}/products`, body: payload }
        : id("slotId") && { method: "POST", path: `${OWNER_PREFIX}/slots/${id("slotId")}/products`, body: payload };
    case MUTATION.UPDATE_PRODUCT:
    case MUTATION.PRODUCT_STATUS:
      return id("productId") && { method: "PATCH", path: `${OWNER_PREFIX}/products/${id("productId")}`, body: payload };
    case MUTATION.SAVE_PRODUCT_CONFIGURATION:
      body = fixedPayload(payload, ["enabled", "groups"]);
      return id("productId") && body && {
        method: "PUT",
        path: `${OWNER_PREFIX}/products/${id("productId")}/configuration`,
        body,
      };
    case MUTATION.SET_SECTION_DEFAULT_MEDIA:
      body = fixedPayload(payload, ["defaultMediaId", "applyToProductsWithoutMedia"]);
      return id("sectionId") && body && {
        method: "PATCH",
        path: `${OWNER_PREFIX}/sections/${id("sectionId")}/default-media`,
        body,
      };
    case MUTATION.DUPLICATE_PRODUCT:
      return id("productId") && { method: "POST", path: `${OWNER_PREFIX}/products/${id("productId")}/duplicate`, body: {} };
    case MUTATION.ASSIGN_SLOT:
      return id("slotId") && { method: "PUT", path: `${OWNER_PREFIX}/slots/${id("slotId")}/product`, body: payload };
    case MUTATION.CLEAR_SLOT:
      return id("slotId") && { method: "DELETE", path: `${OWNER_PREFIX}/slots/${id("slotId")}/product`, body: null };
    case MUTATION.MOVE_SLOT:
      return id("pageId") && { method: "POST", path: `${OWNER_PREFIX}/pages/${id("pageId")}/slots/move`, body: payload };
    case MUTATION.ARCHIVE_MEDIA:
      return id("mediaId") && { method: "POST", path: `${OWNER_PREFIX}/media/${id("mediaId")}/archive`, body: {} };
    case MUTATION.UPDATE_STOREFRONT_SETTINGS:
      body = fixedPayload(payload, [
        "revision", "shopDisplayName", "tagline", "footerStatement", "returnToDccLabel",
        "productCardActionLabel", "productDetailLabels", "defaultBanner", "experienceConfig",
      ], true);
      return body && { method: "PATCH", path: `${OWNER_PREFIX}/storefront/settings`, body };
    case MUTATION.UPDATE_STOREFRONT_HOME:
      body = fixedPayload(payload, ["page", "sections"]);
      return body && (body.page || body.sections)
        ? { method: "PATCH", path: `${OWNER_PREFIX}/storefront/home`, body } : null;
    case MUTATION.CREATE_STOREFRONT_HUB:
      body = fixedPayload(payload, ["displayLabel", "title", "supportingText", "imageMediaId", "color"]);
      return body && { method: "POST", path: `${OWNER_PREFIX}/storefront/departments`, body };    case MUTATION.UPDATE_STOREFRONT_DEPARTMENT:
      body = fixedPayload(payload, ["revision", "displayLabel", "active", "color", "order", "page"], true);
      return id("departmentKey") && body && {
        method: "PATCH", path: `${OWNER_PREFIX}/storefront/departments/${id("departmentKey")}`, body,
      };
    case MUTATION.CREATE_STOREFRONT_DEPARTMENT_CARD: {
      const departmentKey = id("departmentKey");
      const position = Number(args.position);
      body = fixedPayload(payload, ["title", "supportingText", "imageMediaId", "destinationPageId", "enabled"]);
      return departmentKey && Number.isInteger(position) && position >= 1 && position <= 8 && body && {
        method: "POST", path: `${OWNER_PREFIX}/storefront/departments/${departmentKey}/cards/${position}`, body,
      };
    }
    case MUTATION.CLEAR_STOREFRONT_DEPARTMENT_CARD:
      body = fixedPayload(payload, ["revision"], true);
      return id("cardId") && body && { method: "DELETE", path: `${OWNER_PREFIX}/storefront/department-cards/${id("cardId")}`, body };    case MUTATION.UPDATE_STOREFRONT_DEPARTMENT_CARD:
      body = fixedPayload(payload, ["revision", "title", "supportingText", "imageMediaId", "destinationPageId", "enabled", "order"], true);
      return id("cardId") && body && {
        method: "PATCH", path: `${OWNER_PREFIX}/storefront/department-cards/${id("cardId")}`, body,
      };
    case MUTATION.CREATE_STOREFRONT_CATALOGUE:
      body = fixedPayload(payload, ["parentHubKey", "title", "supportingText", "imageMediaId", "sectionId"]);
      return body && { method: "POST", path: `${OWNER_PREFIX}/storefront/catalogues`, body };    case MUTATION.UPDATE_STOREFRONT_CATALOGUE:
      body = fixedPayload(payload, ["revision", "active", "sectionId", "page"], true);
      return id("departmentKey") && id("catalogueKey") && body && {
        method: "PATCH",
        path: `${OWNER_PREFIX}/storefront/catalogues/${id("departmentKey")}/${id("catalogueKey")}`,
        body,
      };
    case MUTATION.UPDATE_STOREFRONT_FACET:
      body = fixedPayload(payload, ["revision", "label", "enabled", "order", "productIds"], true);
      return id("facetId") && body && {
        method: "PATCH", path: `${OWNER_PREFIX}/storefront/facets/${id("facetId")}`, body,
      };
    case MUTATION.UPDATE_STOREFRONT_BANNER:
      body = fixedPayload(payload, [
        "revision", "mode", "enabled", "text", "backgroundColor", "textColor", "speed",
      ], true);
      return id("pageId") && body && {
        method: "PATCH", path: `${OWNER_PREFIX}/storefront/pages/${id("pageId")}/banner`, body,
      };
    case MUTATION.MODERATE_REPORT:
      body = fixedPayload(payload, ["status", "action", "note"]);
      return id("reportId") && body && {
        method: "PATCH", path: `${OWNER_PREFIX}/community/reports/${id("reportId")}`, body,
      };
    default: return null;
  }
}

async function decodeJson(response) {
  const data = await response.json().catch(() => ({}));
  if (response.ok) {
    return { ok: true, state: "ready", reason: null, status: response.status, message: null, data };
  }
  if (response.status === 401) return failure("signed-out", data.error || "signed-out", 401, data.message || null);
  if (response.status === 403) return failure("forbidden", data.error || "forbidden", 403, data.message || null);
  return failure(response.status >= 500 ? "unavailable" : "error", data.error || "unavailable", response.status, data.message || null);
}

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunk = 32 * 1024;
  for (let offset = 0; offset < bytes.length; offset += chunk) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunk));
  }
  return btoa(binary);
}

export function createVirtualShopAdminApi(apiBase, fetchImpl = fetch) {
  if (typeof apiBase !== "string" || !apiBase.trim()) throw new TypeError("An API base is required");
  if (typeof fetchImpl !== "function") throw new TypeError("A fetch implementation is required");
  const base = apiBase.replace(/\/$/, "");

  async function send(path, options = {}) {
    try {
      return await decodeJson(await fetchImpl(`${base}${path}`, {
        credentials: "include",
        ...options,
      }));
    } catch {
      return failure();
    }
  }

  async function read(operation, args = {}) {
    const path = readPath(operation, args);
    return path ? send(path) : failure("error", "invalid-operation");
  }

  async function mutate(operation, args = {}, payload = {}) {
    const spec = mutationSpec(operation, args, payload);
    if (!spec) return failure("error", "invalid-operation");
    const options = { method: spec.method };
    if (spec.body !== null) {
      options.headers = { "Content-Type": "application/json" };
      options.body = JSON.stringify(spec.body);
    }
    return send(spec.path, options);
  }

  async function uploadMedia(file, altText = "") {
    if (!file || !MEDIA_TYPES.has(file.type) || !Number.isSafeInteger(file.size)
      || file.size <= 0 || file.size > MAX_MEDIA_BYTES) {
      return failure("error", "invalid-media");
    }
    const body = new FormData();
    body.append("altText", String(altText || ""));
    body.append("file", file, String(file.name || "image"));
    return send(`${OWNER_PREFIX}/media`, { method: "POST", body });
  }

  async function mediaContent(mediaId) {
    const id = identifier(mediaId);
    if (!id) return failure("error", "invalid-media");
    let response;
    try {
      response = await fetchImpl(`${base}${OWNER_PREFIX}/media/${id}/content`, { credentials: "include" });
    } catch {
      return failure();
    }
    if (!response.ok) return decodeJson(response);
    const contentType = (response.headers.get("content-type") || "").split(";")[0].trim().toLowerCase();
    if (!MEDIA_TYPES.has(contentType)) return failure("unavailable", "invalid-media", response.status);
    const buffer = await response.arrayBuffer();
    if (!buffer.byteLength || buffer.byteLength > MAX_MEDIA_BYTES) return failure("unavailable", "invalid-media", response.status);
    return {
      ok: true, state: "ready", reason: null, status: response.status, message: null,
      data: { contentType, base64: arrayBufferToBase64(buffer) },
    };
  }

  async function previewImport(file, mode = "create") {
    const safeFile = importFile(file);
    if (!safeFile || !IMPORT_MODES.has(mode)) return failure("error", "invalid-import");
    const body = new FormData();
    body.append("file", safeFile, safeFile.name);
    body.append("mode", mode);
    return send(`${OWNER_PREFIX}/imports/preview`, { method: "POST", body });
  }

  async function commitImport(file, { previewId, mode, requestKey } = {}) {
    const safeFile = importFile(file);
    const safePreviewId = previewToken(previewId);
    const safeRequestKey = identifier(requestKey);
    if (!safeFile) return failure("error", "invalid-import");
    if (!safePreviewId) return failure("error", "invalid-preview", null, "Run the dry-run preview again before importing.");
    if (!safeRequestKey || !IMPORT_MODES.has(mode)) return failure("error", "invalid-commit", null, "The approved import request is invalid. Run the dry-run preview again.");
    const body = new FormData();
    body.append("file", safeFile, safeFile.name);
    body.append("previewId", safePreviewId);
    body.append("mode", mode);
    body.append("requestKey", safeRequestKey);
    return send(`${OWNER_PREFIX}/imports/commit`, { method: "POST", body });
  }

  async function previewConfigurationImport(file) {
    const safeFile = importFile(file);
    if (!safeFile) return failure("error", "invalid-import");
    const body = new FormData();
    body.append("file", safeFile, safeFile.name);
    return send(`${OWNER_PREFIX}/configuration-imports/preview`, {
      method: "POST", body,
    });
  }

  async function commitConfigurationImport(file, { previewId, requestKey } = {}) {
    const safeFile = importFile(file);
    const safePreviewId = previewToken(previewId);
    const safeRequestKey = identifier(requestKey);
    if (!safeFile) return failure("error", "invalid-import");
    if (!safePreviewId || !safeRequestKey) {
      return failure(
        "error", "invalid-commit", null,
        "Preview the configuration file again before applying it.",
      );
    }
    const body = new FormData();
    body.append("file", safeFile, safeFile.name);
    body.append("previewId", safePreviewId);
    body.append("requestKey", safeRequestKey);
    return send(`${OWNER_PREFIX}/configuration-imports/commit`, {
      method: "POST", body,
    });
  }

  return Object.freeze({
    read, mutate, uploadMedia, mediaContent, previewImport, commitImport,
    previewConfigurationImport, commitConfigurationImport,
  });
}

export function responseValue(result, keys, fallback) {
  const data = result && result.data;
  if (!data || typeof data !== "object") return fallback;
  for (const key of keys) {
    if (data[key] !== undefined) return data[key];
  }
  return fallback;
}
